/**
 * Reads a claim out of a JWT payload without verifying the signature.
 *
 * That's deliberate, and only safe for claims the app doesn't trust on its own. The
 * `posthog_identity_hash` claim is handed straight to PostHog, which verifies the HMAC
 * server-side against its own secret — a forged or tampered value simply fails there and
 * falls back to anonymous ticket scoping, so there's nothing to gain by faking it locally.
 *
 * The alternative, `supabase.auth.getClaims()`, verifies properly but falls back to a
 * `getUser()` network round trip on projects still using symmetric (HS*) JWT signing — which
 * defeats the point of moving this off a per-boot request in the first place.
 *
 * Never use this for an authorization decision.
 */
export const readUnverifiedTokenClaim = (
  accessToken: string | null | undefined,
  claim: string
): string | null => {
  if (!accessToken) return null;

  const [, payload] = accessToken.split(".");
  if (!payload) return null;

  try {
    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    const decoded = atob(padded);
    const parsed = JSON.parse(decoded) as Record<string, unknown>;
    const value = parsed[claim];

    return typeof value === "string" && value ? value : null;
  } catch {
    return null;
  }
};
